import type {
  BBox,
  Feature,
  FeatureCollection,
  MultiPolygon,
  Polygon,
  Position,
  Properties,
} from "@turf/helpers";
import type { Dispatch, SetStateAction } from "react";

export interface ICollapseElementButtons {
  elementId: string;
  elementName: string;
}

export interface ISoilClassificationProperties {
  label: string;
  [key: string]: any;
  color?: string;
}

export type SoilFeature = Feature<
  Polygon | MultiPolygon,
  ISoilClassificationProperties
>;
export type SoilFeatureCollection = FeatureCollection<
  Polygon | MultiPolygon,
  ISoilClassificationProperties
>;

export interface ISoilHubCollection {
  id: string;
  title: string;
  description: string;
  country: string;
  attribution: string;
  extension: string;
  item_type: string;
  created: string;
  updated: string;
  licence: string;
  organization_id: string;
  field_id: string;
  document_type: string;
  layer_type: string;
  operation_type: string;
  is_published: boolean;
  crs: string;
  data_source: string;
  extend: string;
  platform_name: string;
  soil_sampling_type: string;
  instruments: Instrument[];
  links: Link[];
  keywords: Keyword[];
  providers: Provider[];
  created_by: CreatedBy;
  modified_by: ModifiedBy;
  crop_year: number;
  uploaded_file_type: string;
  external_source_id: string;
  region: string;
  is_suspicious: boolean;
  scan_date: string;
  processing_status: ProcessingStatusType;
  job_executions: JobExecution[];
  classification_level: string;
  is_opted_data_analytics: boolean;
  is_opted_identification: boolean;
  is_opted_marketing: boolean;
  is_opted_profiling: boolean;
  is_opted_ai_training: boolean;
  layer_properties: LayerProperty[];
  usda_soil_classification?: SoilFeatureCollection;
}
interface Instrument {
  id: string;
  instrument_name: string;
}
export interface Link {
  id: string;
  href: string;
  rel: string;
  type: string;
  soil_property_id?: string;
  soil_property_name?: string;
}
interface Keyword {
  id: string;
  keyword: string;
}
interface Provider {
  id: string;
  provider_name: string;
  providers_role: ProvidersRole[];
}
interface ProvidersRole {
  id: string;
  role_name: string;
}
interface CreatedBy {
  created_at: string;
  updated_at: string;
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  is_active: boolean;
}
interface ModifiedBy {
  created_at: string;
  updated_at: string;
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  is_active: boolean;
}
interface JobExecution {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  job_name: string;
}
interface LayerProperty {
  id: string;
  name: string;
  type: string;
  description: string;
  code: string;
  uom?: string;
  property_type?: string;
}

export type ProcessingStatusType = "processing" | "processed" | "failed";

export interface ISoilHubListResponse<T> {
  collections: T[];
}

export interface ICustomSessionStatus {
  config: {
    tags?: string[];
  };
  name: string;
  defaultName: string;
}
export interface IPropertyFileData {
  file_name: string;
  file_url: string;
}
export interface ISamplingPointPayload {
  collectionId: string | undefined;
  measure: string | undefined;
  provider: string | undefined;
  showSamplingPoints?: boolean;
}

export interface CustomFeatureCollection {
  geometry: { coordinates: Position[][] | Position[][][] };
  properties: {
    value: number;
    name: string;
    min_value: number;
    max_value: number;
  };
}

export interface ISamplingPointPromise {
  type: FeatureCollection;
  features: CustomFeatureCollection[];
}

export interface ISoilMapCreateZone {
  setSoilSelectedZone: Dispatch<
    SetStateAction<ISoilMapZoneList | undefined | null>
  >;
}
export interface ISoilMapAnalysisBottomRight {
  zoomedFieldBox: BBox;
  colorRange: number;
  displayColorScale: boolean;
}

export interface ICreateSoilMapZonePayload {
  nutrient_name: string;
  soilhub_collection_id: string;
  assigned_hybrids: null[];
  recommended_hybrid: null[];
  field_id: string;
  field_name: string;
  organization_id: string;
  zone_name: string;
  email_id: string;
  zonesGeometry: FeatureCollection<MultiPolygon | Polygon | Properties>;
  zoneCount: number;
  color_scale: string;
  environment: string;
  year: number;
  number_of_zones: number;
  zone_id?: string;
}

export interface IGetSoilMapAnalysisZonesListPayload {
  fieldId: string;
  soilhubCollectionId: string;
  nutrientName?: string;
}

export interface ISoilMapZoneList {
  id: string;
  name: string;
  type: string;
  generationMethod: string;
  emailId: string;
  createdAt: string;
  updatedAt: string;
  features: Array<Feature<MultiPolygon | Polygon, any>>;
  nutrientName: string;
  // tags: IZoneHistoryTag[];
  isEditable: boolean;
  soilhubCollectionId: string | null;
  numberOfZones: string | null;
}

export interface IElementZoneBox {
  elementId?: string;
  elementName?: string;
  zoneDetails: { id: string; name: string; length: number };
}

export interface IElementZoneBoxButtons
  extends Omit<IElementZoneBox, "zoneDetails"> {
  zoneInfo: { id: string; name: string };
}

export interface IzoneDrawerFormValues {
  zoneName: string;
  numberOfZones: string;
}

interface Groups {
  min: number;
  max: number;
}

export interface ZoneCreationFromSamplingPointsPayload {
  type: string;
  field_border: Position[][] | Position[][][];
  features: Array<{
    geometry: { coordinates: Position[][] | Position[][][] };
    properties: {
      value: number;
      name: string;
      min_value: number;
      max_value: number;
    };
  }>;
  groups?: Groups[];
}

export interface ICollectionListQueryParams {
  page: number;
  limit?: number;
  has_pagination?: boolean;
  countries?: string;
  field_id?: string;
  extension?: string;
  uploaded_by?: string;
  created_at?: string;
  updated_at?: string;
  keywords?: string;
  providers?: string;
  document_type?: string;
  layer_type?: string;
  is_published?: boolean;
  crop_year?: string;
  external_source_id?: string;
  classification_level?: string;
  layer_properties?: string;
}

export enum FileTypeEnum {
  SOILOPTIX = "SoilOptix",
  LAB_FILES = "SoilLabs",
  VERIS = "Veris",
}

export interface ICollectionFileData {
  fileName: string;
  signedUrl: string;
  soilHubCollectionId: string;
  uploadedAt: string;
}

export enum SoilColorPalette {
  NDVI = "NDVI",
  DEFAULT = "DEFAULT",
  ACCESSIBILITY = "ACCESSIBILITY",
}

export interface RasterStats {
  min: number;
  max: number;
  mean: number;
  stdDev: number;
  histogram?: {
    bins: number[];
    counts: number[];
  };
}

export type SOIL_VARIABILITY_LEVEL = "LOW" | "MEDIUM" | "HIGH";
